<template>
  <div>
    <div v-if="!support" class="text-center">
      <h3 class="text-danger">Your browser does not support video recording.</h3>
    </div>
    <div v-if="support" ref="video-wrapper" class="video-wrapper">
      <div
        v-show="state === states.COUNTING_DOWN"
        class="countdown position-absolute inset-0 w-100 h-100"
      >
        <span class="text-white position-absolute inset-0 w-50px h-fit m-auto text-center">{{ countdownSec }}</span>
      </div>
      <div
        v-if="isLoadingVirtualBackground"
        class="tw-absolute tw-w-fit tw-h-fit tw-inset-0 tw-top-1/2 tw-left-1/2 tw-text-white tw--translate-x-1/2 tw--translate-y-1/2"
      >
        <span class="fa fa-spinner fa-spin fa-3x"></span>
      </div>
      <video
        ref="video"
        class="player d-none"
        autoplay
        muted
      />
      <canvas
        ref="canvasRef"
        class="w-100 cursor-move"
      />
      <webcam-recorder-start-recording-button
        v-if="state === states.READY && fullPermission && webcamReady && !isLoadingVirtualBackground"
        class="dubb-video-preview-btn inset-0 position-absolute m-auto"
        @click="countdown()"
      />
      <div v-if="duration" class="duration-wrapper position-absolute mx-auto left-0 right-0 w-fit rounded">
        <span class="text-white m-1">{{ duration }}</span>
      </div>
      <div
        v-if="state === states.RECORDING || state === states.PAUSED || state === states.COUNTING_DOWN"
        class="d-flex align-items-end justify-content-center position-absolute bottom-0 left-0 right-0 mx-auto mb-4"
      >
        <webcam-recorder-resume-recording-button
          v-if="state === states.PAUSED"
          class="dubb-video-preview-btn mx-1"
          @click="resume()"
        />

        <webcam-recorder-pause-recording-button
          v-else
          class="dubb-video-preview-btn mx-1"
          @click="pause()"
        />

        <webcam-recorder-cancel-recording-button
          class="dubb-video-preview-btn mx-1"
          @click="cancel()"
        />

        <webcam-recorder-upload-recording-button
          class="dubb-video-preview-btn mx-1"
          @click="stop()"
        />
      </div>
      <div v-if="simple && webcamReady && state === states.READY" class="setting-toggler position-absolute d-flex justify-content-center align-items-center m-4 bottom-0 right-0 p-1">
        <a class="btn btn-transparent btn-xs btn-icon cursor-pointer" @click="showSettings=!showSettings"><i class="fal fa-cogs"></i></a>
      </div>

      <webcam-recorder-transform-controls class="dubb-video-transform-controls position-absolute left-0 bottom-0 m-4" />
    </div>
    <form v-show="showSettings && state === states.READY && fullPermission" class="mt-4">
      <div class="row">
        <div class="form-group col-sm-4">
          <label class="hint--bottom hint--large hint--rounded" aria-label="The Dubb video system uses your computer's camera in order to record an asynchronous video. You will need to grant access the first time you use the system in your System Preferences. If you are having trouble connecting your camera, please read this support article.">
            WebCam
            <a href="https://support.dubb.com/en/articles/5764910-how-to-allow-webcam-and-mic-access-to-the-dubb-chrome-extension" target="_blank" rel="noopener noreferrer">
              <i class="fad fa-1x fa-info-circle"></i>
            </a>
          </label>
          <select v-model="selectedCamera" class="form-control">
            <option v-for="item in cameraDevices" :value="item.deviceId">{{ item.label }}</option>
          </select>
        </div>
        <div class="form-group col-sm-4">
          <div style="display: flex; flex-direction: row; justify-content: space-between; align-items: center;">
            <label class="hint--bottom hint--large hint--rounded" aria-label="The Dubb video system uses your computer's microphone in order to record an asynchronous video. You will need to grant access the first time you use the system in your System Preferences. If you are having trouble connecting your microphone, please read this support article.">
              Microphone
              <a href="https://support.dubb.com/en/articles/5764910-how-to-allow-webcam-and-mic-access-to-the-dubb-chrome-extension" target="_blank" rel="noopener noreferrer">
                <i class="fad fa-1x fa-info-circle"></i>
              </a>
            </label>
            <div v-if="showVoiceMeter" class="progress" style="height: 8px; flex: 1; margin-bottom: 0; margin-left: 10px;">
              <div class="bg-success" style="height: 100%;" :style="{width: audioVolume + '%'}"></div>
            </div>
          </div>
          <select v-model="selectedMicrophone" class="form-control">
            <option v-for="item in audioDevices" :value="item.deviceId">{{ item.label }}</option>
          </select>
        </div>
        <div v-if="!simple" class="form-group col-sm-4">
          <label class="hint--bottom hint--large hint--rounded" aria-label="Higher resolution results in slower uploading and transcoding speeds.">Quality <i class="fad fa-1x fa-info-circle" aria-hidden="true"></i></label>
          <select v-model="quality" class="form-control" :disabled="guest" @change="reloadDevices()">
            <option v-for="(item, key) in qualities" :value="key">{{ item.width }}x{{ item.height }} ({{ item.name }})</option>
          </select>
        </div>
      </div>
      <div v-if="!simple" class="row">
        <div class="col-md-4">
          <div class="checkbox-inline">
            <label class="checkbox checkbox-success mb-0">
              <input
                v-model="isMirrorVideo"
                type="checkbox"
              >
              <span></span>
              Mirror my video
            </label>
          </div>
        </div>
        <div v-if="!isHideTeleprompter" class="col-md-4">
          <div class="checkbox-inline">
            <label class="checkbox checkbox-success">
              <input v-model="isDisplayTeleprompter" type="checkbox">
              <span></span>
              Display Teleprompter
            </label>
          </div>
        </div>
      </div>
      <div v-if="!simple" class="row">
        <div class="col-md-4 d-flex align-items-center">
          <div class="checkbox-inline mr-1">
            <label class="checkbox checkbox-success">
              <input id="checkbox-vb-bokeh" v-model="vbBokeh" type="checkbox" :disabled="vbNet === null || !webcamReady">
              <span></span>
              Blur Background
            </label>
          </div>
          <div class="hint--top hint--large" aria-label="This feature allows you to blur your existing background. Here are minimum system requirements: MAC OS 13, Windows 10 64 bit, 8GB of ram, and i7 dual core CPU.">
            <i class="fad  fa-1x fa-info-circle" aria-hidden="true"></i>
          </div>
        </div>
        <div class="col-md-4 d-flex align-items-center">
          <div class="checkbox-inline mr-1">
            <label class="checkbox checkbox-success">
              <input id="checkbox-vb-mode" v-model="vbMode" type="checkbox" :disabled="vbNet === null || !webcamReady">
              <span></span>
              Virtual Background
            </label>
          </div>
          <rich-tooltip
            read-more="https://support.dubb.com/en/articles/5764788-how-to-use-dubb-virtual-backgrounds"
            text="This feature allows you to add virtual backgrounds. Here are minimum system requirements: MAC OS 13, Windows 10 64 bit, 8GB of ram, and i7 dual core CPU."
          >
            <i class="fad fa-1x fa-info-circle" aria-hidden="true"></i>
          </rich-tooltip>
        </div>
        <div v-if="vbBokeh" class="col-md-4 d-flex align-items-center">
          <span class="mr-3">Blur Amount</span>
          <input v-model.number="vbBokehBgBlurAmount" type="range" min="1" max="30">
        </div>
        <div v-if="vbMode" class="col-md-4 d-flex align-items-center">
          <span class="mr-3">Edge Blur</span>
          <input v-model.number="vbBgEdgeBlurAmount" type="range" min="1" max="20">
        </div>
      </div>
      <virtual-background-image-picker
        v-if="!simple"
        v-show="vbMode"
        v-model="vbBgImgUrl"
        class="mt-3"
        :visible="vbMode"
        :guest="guest"
      ></virtual-background-image-picker>
    </form>
    <teleprompter v-if="support && isDisplayTeleprompter && !isHideTeleprompter && teamHasFeature(constants.GLOBAL.FEATURE_TELEPROMPTER_SCRIPT)" ref="teleprompter" />
  </div>
</template>

<script>
import { markRaw } from 'vue'
import _pick from 'lodash/pick'
import { TwilioVirtualBackground } from '@dubb-app/virtual-background'
import VirtualBackgroundImagePicker from './virtual-background-image-picker.vue'
import Teleprompter from './teleprompter.vue'
import WebcamRecorderStartRecordingButton from './webcam-recorder/webcam-recorder-start-recording-button.vue'
import RecorderMixin from './recorder-mixin'
import VoiceMeter from './voice-meter'
import { VideoMonoStream } from './video-mono-stream.js'
import { is_touchable_device, resolveVendorResource } from '@/utils'
import * as icons from '../../common/icons'
import WebcamRecorderUploadRecordingButton from './webcam-recorder/webcam-recorder-upload-recording-button.vue'
import WebcamRecorderCancelRecordingButton from './webcam-recorder/webcam-recorder-cancel-recording-button.vue'
import WebcamRecorderResumeRecordingButton from './webcam-recorder/webcam-recorder-resume-recording-button.vue'
import WebcamRecorderPauseRecordingButton from './webcam-recorder/webcam-recorder-pause-recording-button.vue'
import WebcamRecorderTransformControls from './webcam-recorder/webcam-recorder-transform-controls.vue'
import { useWebcamRecorder } from './webcam-recorder/use-webcam-recorder'
import { useWebcamRecorderCanvas } from './webcam-recorder/use-webcam-recorder-canvas'

import raf from 'raf'
import RecordRTC from 'recordrtc'
import 'webrtc-adapter'

window.RecordRTC = RecordRTC

const states = {
  READY: 'ready',
  COUNTING_DOWN: 'counting_down',
  RECORDING: 'recording',
  PAUSED: 'paused'
}

const FPS = 30
const FPS_INTERVAL = 1000 / FPS

export default {
  name: 'WebcamRecorder',
  components: {
    WebcamRecorderTransformControls,
    WebcamRecorderPauseRecordingButton,
    WebcamRecorderResumeRecordingButton,
    WebcamRecorderCancelRecordingButton,
    WebcamRecorderUploadRecordingButton,
    WebcamRecorderStartRecordingButton,
    VirtualBackgroundImagePicker,
    Teleprompter
  },
  mixins: [RecorderMixin],
  props: {
    guest: {
      type: Boolean,
      default: false
    },
    simple: {
      type: Boolean,
      default: false
    },
    hideTeleprompter: {
      type: Boolean,
      default: false
    }
  },
  emits: ['record-finished', 'ready', 'unsupported'],
  setup () {
    // default quality 720p
    const offscreenCanvas = new OffscreenCanvas(1280, 720)

    const {
      videoQuality,
      isMirrorVideo,
      isDisplayTeleprompter,
      isVirtualBackgroundBlur,
      blurAmount,
      isVirtualBackgroundImage,
      virtualBackgroundImage,
      edgeBlurAmount,
      ...rest
    } = useWebcamRecorder()

    useWebcamRecorderCanvas()

    return {
      ...rest,
      videoQuality,
      isMirrorVideo,
      isDisplayTeleprompter,
      vbBokeh: isVirtualBackgroundBlur,
      vbBokehBgBlurAmount: blurAmount,
      vbMode: isVirtualBackgroundImage,
      vbBgImgUrl: virtualBackgroundImage,
      vbBgEdgeBlurAmount: edgeBlurAmount,

      offscreenCanvas
    }
  },
  data: () => ({
    icons,
    states,
    showSettings: true,
    voiceMeter: null,
    videoMonoStream: null,
    showVoiceMeter: false,
    audioVolume: 0,
    countdownSec: 3,
    cameraDevices: [],
    audioDevices: [],
    selectedCamera: null,
    selectedMicrophone: null,
    duration: null,
    timeParts: [],
    state: null,
    support: true,
    fullPermission: false,
    webcamReady: false,
    currentRaf: null,
    vbLoaded: false,
    /** @type {TwilioVirtualBackground} */
    vbNet: null,
    quality: '720p',
    qualities: {
      '1080p': { width: 1920, height: 1080, name: 'Full HD' },
      '720p': { width: 1280, height: 720, name: 'High Definition' },
      SVGA: { width: 800, height: 600, name: 'Standard Definition' },
      VGA: { width: 640, height: 480, name: 'Normal Definition' },
      '360p': { width: 640, height: 360, name: 'Lightweight' }
    },
    lastTimestamp: null
  }),
  computed: {
    selectedQuality () {
      return this.qualities[this.quality] || this.qualities['720p']
    },
    isHideTeleprompter () {
      return this.guest || this.simple || this.hideTeleprompter
    },
    isVirtualBackground () {
      return this.vbMode || this.vbBokeh
    },
    isLoadingVirtualBackground () {
      return !this.vbLoaded && this.isVirtualBackground
    }
  },
  watch: {
    selectedCamera (newVal, oldVal) {
      this.cameraDeviceId = newVal
      if (newVal && oldVal) {
        this.reloadDevices()
      }
    },
    selectedMicrophone (newVal) {
      this.microphoneDeviceId = newVal
    },
    isMirrorVideo (newVal) {
      this.vbNet.isMirrorVideo = newVal
    },
    vbMode (newVal) {
      if (newVal) {
        this.vbBokeh = false

        if (this.vbBgImgUrl) {
          this.vbNet.setProcessor({
            type: 'image',
            backgroundImage: this.vbBgImgUrl
          })
        }
      }
    },
    vbBokeh (newVal) {
      if (newVal) {
        this.vbMode = false
        this.vbNet.setProcessor({
          type: 'blur'
        })
      }
    },
    vbBgImgUrl (newVal) {
      if (this.vbMode) {
        this.vbNet.setProcessor({
          type: 'image',
          backgroundImage: newVal
        })
      }
    },
    isVirtualBackground (newVal) {
      if (!newVal) {
        this.vbNet?.setProcessor({
          type: 'none'
        })
      }
    },
    vbBgEdgeBlurAmount (newVal) {
      this.vbNet.edgeBlurAmount = newVal
    },
    vbBokehBgBlurAmount (newVal) {
      this.vbNet.backgroundBlurAmount = newVal
    }
  },
  created () {
    this.showSettings = !this.simple
    if (this.guest || this.simple) {
      this.quality = '360p'
      this.qualities = _pick(this.qualities, ['360p'])
    }
  },
  mounted () {
    if (!navigator.mediaDevices || is_touchable_device() || typeof MediaRecorder !== 'function') {
      this.support = false
      this.$emit('unsupported')
      return
    }
    const quality = this.videoQuality
    if (!this.guest && quality) {
      this.quality = quality
    }
    this.setState(states.READY)

    this.$emit('ready')
  },
  methods: {
    capture (cb = null) {
      if (!this.support) {
        return
      }
      const previousVbMode = this.vbMode
      const previousVbBokeh = this.vbBokeh

      if (previousVbMode) {
        this.vbMode = false
      }
      if (previousVbBokeh) {
        this.vbBokeh = false
      }

      const constraints = {
        audio: {
          echoCancellation: false
        },
        video: {
          width: { ideal: this.selectedQuality.width },
          height: { ideal: this.selectedQuality.height }
        }
      }
      if (this.selectedCamera) {
        constraints.video.deviceId = { exact: this.selectedCamera }
      }
      if (this.selectedMicrophone) {
        constraints.audio.deviceId = { exact: this.selectedMicrophone }
      }
      if (this.$refs.video.srcObject) {
        this.$refs.video.srcObject.getTracks().forEach(track => {
          track.stop()
        })
      }
      navigator.mediaDevices.getUserMedia(constraints)
        .then((originalStream) => {
          if (!this.fullPermission) {
            this.reloadDevices(false)
          }

          this.videoMonoStream = new VideoMonoStream(originalStream)

          const stream = this.videoMonoStream.getStream()

          this.voiceMeter = new VoiceMeter(stream)
          this.showVoiceMeter = !this.simple && this.voiceMeter.isSupported
          this.voiceMeter.onChange(volume => {
            this.audioVolume = parseInt(volume)
          })

          this.$refs.video.srcObject = stream
          this.$refs.video.muted = true
          this.$refs.video.play() // This line is required when the <video> is invisible
          this.$refs.video.onloadeddata = () => {
            this.webcamReady = true
            this.$refs.video.width = this.$refs.video.videoWidth
            this.$refs.video.height = this.$refs.video.videoHeight
            this.canvasRef.width = this.$refs.video.videoWidth
            this.canvasRef.height = this.$refs.video.videoHeight

            this.offscreenCanvas.width = this.$refs.video.videoWidth
            this.offscreenCanvas.height = this.$refs.video.videoHeight

            this.vbNet = markRaw(new TwilioVirtualBackground({
              canvas: this.offscreenCanvas,
              video: this.$refs.video,
              isVirtualBackgroundImage: this.vbMode,
              isMirrorVideo: this.isMirrorVideo,
              edgeBlurAmount: this.vbBgEdgeBlurAmount,
              backgroundBlurAmount: this.vbBokehBgBlurAmount,
              assetsPath: resolveVendorResource('@twilio/video-processors')
            }))

            if (this.vbMode) {
              this.vbNet.setProcessor({
                type: 'image',
                backgroundImage: this.vbBgImgUrl
              })
            } else if (this.vbBokeh) {
              this.vbNet.setProcessor({
                type: 'blur'
              })
            } else {
              this.vbNet.setProcessor({
                type: 'none'
              })
            }

            if (this.currentRaf) {
              raf.cancel(this.currentRaf)
            }

            this.renderCanvasFrame()

            if (previousVbMode) {
              this.vbMode = true
            }
            if (previousVbBokeh) {
              this.vbBokeh = true
            }
          }
          if (cb) {
            cb()
          }
          this.videoQuality = this.quality
        })
        .catch(e => {
          console.log(e, constraints, e.name)
          if (e?.name === 'OverconstrainedError') {
            window.SwalTimer.fire({
              icon: 'warning',
              title: 'Please select a lower video resolution to work with your webcam.'
            }).then(() => {
              // this.videoQuality = '360p'
              // location.reload()
            })

            return
          }

          console.error(e)
          window.SwalAlert.fire({
            icon: 'error',
            title: e?.message || 'There is something wrong when get the user media'
          })
        })
    },
    stopCapture () {
      this.setState(null)

      if (!this.support) {
        return
      }

      if (!this.$refs.video.srcObject) {
        return
      }

      this.$refs.video.srcObject.getTracks().forEach(track => {
        track.stop()
      })

      if (this.voiceMeter) {
        this.voiceMeter.stop()
      }

      if (this.videoMonoStream) {
        this.videoMonoStream.stop()
      }
    },
    countdown () {
      if (!this.fullPermission) {
        this.reloadDevices(false, (ret) => {
          if (ret) {
            this.countdown()
          }
        })
        return
      }

      this.setState(states.COUNTING_DOWN)
      this.capture()
      this.countdownSec = 3
      const hdl = setInterval(() => {
        if (this.countdownSec === 0) {
          clearInterval(hdl)
          this.start()

          return
        }
        this.countdownSec--
      }, 1000)
    },
    start () {
      if (this.state !== states.COUNTING_DOWN) {
        return
      }

      const videoStream = this.$refs.video.srcObject

      const stream = this.canvasRef.captureStream(FPS)
      if (videoStream) {
        const audioTracks = videoStream.getAudioTracks()
        audioTracks.forEach(track => {
          stream.addTrack(track)
        })
      }

      if (!stream) {
        this.support = false
        return
      }

      this.startRecorder(stream)

      this.timeParts = [{ start: new Date().getTime() }]
      this.setState(states.RECORDING)
      this.refreshDuration()
      this.playTeleprompter()
    },

    async cancel () {
      this.pause()
      const res = await window.SwalConfirm.fire({
        icon: 'warning',
        title: 'Are you sure?',
        confirmButtonText: 'Yes, cancel this recording',
        cancelButtonText: 'No'
      })

      if (res.isConfirmed) {
        await this.stop(false)
        return
      }

      this.resume()
    },
    async stop (save = true) {
      if (this.state !== states.PAUSED && this.state !== states.RECORDING) {
        return
      }

      const file = await this.stopRecorder(save)
      if (save) {
        this.$emit('record-finished', file, this.constants.App_Models_Video.SOURCE_TYPE_RECORD)
      }

      this.setState(states.READY)
      this.pauseTeleprompter()
    },
    pause () {
      if (this.state !== states.RECORDING) {
        return
      }
      this.recorder.pauseRecording()
      this.setState(states.PAUSED)
      const lastPart = this.timeParts[this.timeParts.length - 1]
      lastPart.end = new Date().getTime()
      lastPart.duration = lastPart.end - lastPart.start
      this.pauseTeleprompter()
    },
    resume () {
      if (this.state !== states.PAUSED) {
        return
      }
      this.recorder.resumeRecording()
      this.setState(states.RECORDING)
      this.timeParts.push({ start: new Date().getTime() })
      this.resumeTeleprompter()
    },
    setState (state) {
      this.state = state
    },
    reloadDevices (capture = true, cb = null) {
      if (!this.support) {
        return
      }
      this.getAllAudioVideoDevices(result => {
        this.cameraDevices = result.videoInputDevices
        this.audioDevices = result.audioInputDevices
        let fullPermission = true
        if (result.audioInputDevices.length > 0) {
          if (!this.selectedMicrophone) {
            const audioId = this.microphoneDeviceId
            if (audioId && result.audioInputDevices.findIndex(d => d.id === audioId) !== -1) {
              this.selectedMicrophone = audioId
            } else {
              this.selectedMicrophone = result.audioInputDevices[0].id
            }
          }
          if (!result.audioInputDevices[0].label) {
            fullPermission = false
          }
        } else {
          fullPermission = false
        }
        if (result.videoInputDevices.length > 0) {
          if (!this.selectedCamera) {
            const videoId = this.cameraDeviceId
            if (videoId && result.videoInputDevices.findIndex(d => d.id === videoId) !== -1) {
              this.selectedCamera = videoId
            } else {
              this.selectedCamera = result.videoInputDevices[0].id
            }
          }
          if (!result.videoInputDevices[0].label) {
            fullPermission = false
          }
        } else {
          fullPermission = false
        }
        this.fullPermission = fullPermission
        this.setState(states.READY)
        if (capture) {
          this.capture()
        }
        if (cb) {
          cb(fullPermission)
        }
      })
    },
    getAllAudioVideoDevices (successCallback, failureCallback) {
      const allMediaDevices = []
      const allAudioDevices = []
      const allVideoDevices = []

      const audioInputDevices = []
      const audioOutputDevices = []
      const videoInputDevices = []

      navigator.mediaDevices.enumerateDevices()
        .then((devices) => {
          devices.forEach((_device) => {
            const device = {}
            for (const d in _device) {
              device[d] = _device[d]
            }

            // make sure that we are not fetching duplicate devics
            let skip
            allMediaDevices.forEach((d) => {
              if (d.id === device.id) {
                skip = true
              }
            })

            if (skip) {
              return
            }

            // if it is MediaStreamTrack.getSources
            if (device.kind === 'audio') {
              device.kind = 'audioinput'
            }

            if (device.kind === 'video') {
              device.kind = 'videoinput'
            }

            if (!device.deviceId) {
              device.deviceId = device.id
            }

            if (!device.id) {
              device.id = device.deviceId
            }

            if (device.kind === 'audioinput' || device.kind === 'audio') {
              audioInputDevices.push(device)
            }

            if (device.kind === 'audiooutput') {
              audioOutputDevices.push(device)
            }

            if (device.kind === 'videoinput' || device.kind === 'video') {
              videoInputDevices.push(device)
            }

            if (device.kind.indexOf('audio') !== -1) {
              allAudioDevices.push(device)
            }

            if (device.kind.indexOf('video') !== -1) {
              allVideoDevices.push(device)
            }
            allMediaDevices.push(device)
          })

          if (successCallback) {
            successCallback({
              allMediaDevices,
              allVideoDevices,
              allAudioDevices,
              videoInputDevices,
              audioInputDevices,
              audioOutputDevices
            })
          }
        })
        .catch(() => {
          failureCallback()
        })
    },
    refreshDuration () {
      if (this.state !== states.RECORDING && this.state !== states.PAUSED) {
        this.duration = null
        this.timeParts = []
        return
      }

      this.duration = this.calculateTimeDuration(this.timeParts.reduce((carry, part) => {
        return carry + (part.duration ? part.duration : (new Date().getTime() - part.start))
      }, 0) / 1000)
      setTimeout(() => {
        this.refreshDuration()
      }, 500)
    },
    calculateTimeDuration (secs) {
      const hr = Math.floor(secs / 3600)
      let min = Math.floor((secs - (hr * 3600)) / 60)
      let sec = Math.floor(secs - (hr * 3600) - (min * 60))

      if (min < 10) {
        min = '0' + min
      }

      if (sec < 10) {
        sec = '0' + sec
      }

      if (hr <= 0) {
        return min + ':' + sec
      }

      return hr + ':' + min + ':' + sec
    },

    playTeleprompter () {
      const ref = this.$refs.teleprompter
      if (ref && ref.scriptSelected) {
        ref.scrollFromTop()
      }
    },

    resumeTeleprompter () {
      const ref = this.$refs.teleprompter
      if (ref && ref.scriptSelected) {
        ref.scroll()
      }
    },

    pauseTeleprompter () {
      const ref = this.$refs.teleprompter
      if (ref && ref.scriptSelected) {
        ref.pause()
      }
    },

    async renderCanvasFrame () {
      if (!this.lastTimestamp) {
        this.lastTimestamp = Date.now()
      }

      const now = Date.now()
      const delta = now - this.lastTimestamp

      if (delta > FPS_INTERVAL) {
        if (this.$refs.video.readyState > 2) {
          await this.vbNet.draw()

          this.vbLoaded = true
        }

        this.drawOffscreenCanvas()

        this.lastTimestamp = now - (delta % FPS_INTERVAL)
      }

      this.currentRaf = raf(() => {
        this.renderCanvasFrame()
      })
    },

    drawOffscreenCanvas () {
      if (!this.canvasRef) {
        return
      }

      const canvasElement = this.canvasRef
      const ctx = canvasElement.getContext('2d')

      ctx.clearRect(0, 0, canvasElement.width, canvasElement.height)

      ctx.save()
      const translateX = this.webcamTranslate.x
      const translateY = this.webcamTranslate.y
      const scaleX = this.webcamScale
      const scaleY = this.webcamScale

      ctx.scale(scaleX, scaleY)
      ctx.translate(translateX, translateY)

      ctx.drawImage(
        this.offscreenCanvas,
        canvasElement.width * (1 - scaleX) / 2 / scaleX,
        canvasElement.height * (1 - scaleY) / 2 / scaleY
      )

      ctx.restore()
    }

  }
}
</script>

<style scoped lang="scss">
  .video-wrapper {
    position: relative;

    & > div {
      z-index: 97;

      &.setting-toggler {
        background: rgba(0,0,0,0.5);
      }
    }

    .countdown {
      background: rgba(0, 0, 0, .5);

      span {
        font-size: 42px;
      }
    }

    .duration-wrapper {
      bottom: 70px;
      background: rgba(0, 0, 0, .5);
      span {
        font-size: 24px;
      }
    }

    canvas {
      background-color: black;
    }
  }

  .player {
    width: 100%;
    height: 100%;
    background: black;
  }

  .dubb-video-preview-btn {
    width: 36px;
    height: 36px;
    line-height: 16px;
    background-color: #e7e7e7;
    border: none;
    color: black;
    text-align: center;
    text-decoration: none;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    font-size: 16px;
    cursor: pointer;
    border-radius: 50% !important;

    &:hover {
      background: white;
    }
  }

  .dubb-video-transform-controls {
    background: rgba(0, 0, 0, .5);
  }
</style>
